import {useEffect,useRef,useState} from 'react';
import api from '@/lib/api';
import {Button} from '@/components/ui/button';
import {Sparkles,Check,X} from 'lucide-react';
import {toast} from 'sonner';
// ---------------- AI COPILOT ----------------
export default function AiCopilot({ id, reload, preferred = [] }) {
  const [loading, setLoading] = useState("");
  const [result, setResult] = useState(null);
  const [feature, setFeature] = useState("");
  const [editing,setEditing]=useState(null),[revised,setRevised]=useState(''),[reason,setReason]=useState(''),[saving,setSaving]=useState(false);
  const seq=useRef(0);
  useEffect(()=>{seq.current++;setLoading('');setResult(null);setFeature('');setEditing(null);setRevised('');setReason('');setSaving(false);return()=>{seq.current++;};},[id]);

  const features = [
    { key: "risk_candidates", label: "Kandidat Risiko" },
    { key: "missing_procedures", label: "Prosedur yang Hilang" },
    { key: "inquiry_consistency", label: "Konsistensi Inkuiri Kecurangan" },
    { key: "wp_review", label: "Reviu Kertas Kerja" },
    { key: "sampling_recommendation", label: "Rekomendasi Sampling" },
    { key: "jet_analysis", label: "Analisis JET" },
    { key: "fs_calk_review", label: "Reviu LK / CALK" },
    { key: "cross_reference", label: "Konsistensi Lintas-Referensi" },
    { key: "missing_evidence", label: "Deteksi Bukti Hilang" },
    { key: "followup_procedures", label: "Prosedur Tindak Lanjut" },
    { key: "status_summary", label: "Ringkasan Status Audit" },
  ];

  const run = async (key) => {
    const owner=++seq.current;setLoading(key);setResult(null);setFeature(key);setEditing(null);setSaving(false);
    try {
      const { data } = await api.post(`/engagements/${encodeURIComponent(id)}/ai/${key}`);
      if(owner!==seq.current)return;
      if(data.feature!==key)throw Error('Identitas hasil AI tidak sesuai dengan permintaan.');
      const sug=data.result?.suggestions??(typeof data.result?.raw==='string'?[{title:'Hasil',detail:data.result.raw}]:[]);
      if(!Array.isArray(sug)||sug.length>100||JSON.stringify(sug).length>1000000||sug.some(s=>!s||typeof s!=='object'||Array.isArray(s)||['title','name','detail','description','assertion','account','risk_id'].some(k=>s[k]!=null&&typeof s[k]!=='string')))throw Error('Format hasil AI tidak dapat ditampilkan dengan aman. Muat ulang permintaan.');
      setResult(sug);
    } catch (e) {if(owner===seq.current){setResult(null);toast.error(e.response?.data?.detail||e.message||'AI gagal merespons');}}
    finally {if(owner===seq.current)setLoading('');}
  };

  const decide = async (suggestion, decision, note='') => {
    const owner=seq.current;setSaving(true);
    try{
      await api.post(`/engagements/${encodeURIComponent(id)}/ai-decision`, {feature,suggestion:JSON.stringify(suggestion),decision,note});
      if(owner!==seq.current)return;
      setEditing(null);toast.success(`Keputusan "${decision}" tercatat di log audit; status pengujian tidak berubah.`);reload();
    }catch(e){if(owner===seq.current)toast.error(e.response?.data?.detail||'Keputusan belum tersimpan.');}
    finally{if(owner===seq.current)setSaving(false);}
  };
  const modify=s=>{setEditing(s);setRevised(s.detail||s.description||JSON.stringify(s));setReason('');};
  const saveModification=()=>decide(editing,'Modify',JSON.stringify({revised_text:revised.trim(),reason:reason.trim()}));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-head text-lg font-semibold">AI Audit Assistant</span>
          <span className="text-[10px] px-1.5 py-0.5 border border-indigo-300 rounded-sm bg-indigo-50 text-indigo-700 font-medium" data-testid="ai-powered-by">Powered by GPT</span>
        </div>
      </div>
      <div className="bg-indigo-50 border border-indigo-200 rounded-sm p-3 flex items-start gap-2 text-sm text-indigo-800">
        <Sparkles size={16} className="mt-0.5" /> <span><span className="font-medium">AI Suggestion — Auditor Judgment Required.</span> AI adalah pendukung keputusan; tidak dapat menandatangani KKA, menyetujui independensi, menyimpulkan kecurangan secara final, melakukan persetujuan EQR, menentukan opini audit, atau merilis laporan. Setiap saran material wajib di-Terima/Modifikasi/Tolak.</span>
      </div>
      {preferred.length > 0 && <div className="text-xs text-muted-foreground" data-testid="ai-preferred-note">Disarankan untuk halaman ini ditampilkan lebih dulu.</div>}
      <div className="flex flex-wrap gap-2">
        {[...features].sort((a, b) => (preferred.includes(a.key) ? 0 : 1) - (preferred.includes(b.key) ? 0 : 1)).map((ft) => (
          <Button key={ft.key} variant="outline" size="sm" onClick={() => run(ft.key)} disabled={!!loading||saving} data-testid={`ai-${ft.key}`}>
            <Sparkles size={14} className="mr-1" />{loading === ft.key ? "Menganalisis..." : ft.label}
          </Button>
        ))}
      </div>
      {editing&&<section aria-label="Modifikasi saran AI" className="border rounded p-3 space-y-2"><h4>Catat modifikasi auditor</h4><p>Saran asli tetap disimpan. Modifikasi ini mencatat penilaian auditor dan tidak mengubah hasil pengujian.</p><label className="block">Saran setelah modifikasi<textarea aria-label="Saran setelah modifikasi" maxLength={4000} className="block border w-full p-2" value={revised} onChange={e=>setRevised(e.target.value)}/></label><label className="block">Alasan modifikasi<textarea aria-label="Alasan modifikasi" maxLength={4000} className="block border w-full p-2" value={reason} onChange={e=>setReason(e.target.value)}/></label><Button data-testid="ai-save-modification" disabled={saving||!reason.trim()||!revised.trim()||revised.trim()===(editing.detail||editing.description||JSON.stringify(editing)).trim()} onClick={saveModification}>Simpan modifikasi</Button><Button disabled={saving} onClick={()=>setEditing(null)}>Batal</Button></section>}
      {result && (
        <div className="space-y-3">
          {result.length === 0 && <div className="text-sm text-muted-foreground">Tidak ada saran.</div>}
          {result.map((s, i) => (
            <div key={i} className="bg-white border border-indigo-200 rounded-sm p-4" data-testid={`ai-suggestion-${i}`}>
              <div className="text-[10px] uppercase tracking-wider text-indigo-600 font-semibold mb-1">AI Suggestion — Auditor Judgment Required</div>
              <div className="font-medium text-sm">{s.title || s.name || `Saran ${i + 1}`}</div>
              <div className="text-sm text-muted-foreground mt-1">{s.detail || s.description || JSON.stringify(s)}</div>
              {(s.assertion || s.account || s.risk_id) && (
                <div className="text-xs text-muted-foreground mt-1">{s.account && `Akun: ${s.account} · `}{s.assertion && `Asersi: ${s.assertion}`}{s.risk_id && ` · Risk: ${s.risk_id}`}</div>
              )}
              <div className="flex gap-2 mt-3">
                <Button size="sm" className="h-7" disabled={saving} onClick={() => decide(s, "Accept")} data-testid={`ai-accept-${i}`}><Check size={13} className="mr-1" />Terima</Button>
                <Button size="sm" variant="outline" className="h-7" disabled={saving} onClick={() => modify(s)} data-testid={`ai-modify-${i}`}>Modifikasi</Button>
                <Button size="sm" variant="outline" className="h-7 text-destructive" disabled={saving} onClick={() => decide(s, "Reject")} data-testid={`ai-reject-${i}`}><X size={13} className="mr-1" />Tolak</Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
